<template>
  <div class="d-flex flex-column ga-4">
    <v-alert type="info">
      {{ $t("ncrm.workflows.nodeSettings.enrichment.bestPractice") }}
    </v-alert>

    <div class="d-flex flex-wrap ga-2">
      <v-btn color="secondary" variant="tonal" @click="$emit('open-ai-workspace')">
        <v-icon start>mdi-open-in-new</v-icon>
        {{ $t("ncrm.workflows.actions.openAiWorkspace") }}
      </v-btn>
      <v-btn color="primary" variant="text" :loading="loadingAiPrompts" @click="$emit('refresh-ai-prompts')">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("app.actions.refresh") }}
      </v-btn>
    </div>

    <v-alert v-if="aiPromptsError" type="warning" density="compact">
      {{ aiPromptsError }}
    </v-alert>

    <v-row dense>
      <v-col cols="12" md="4">
        <v-switch
          v-model="form.status"
          :true-value="'active'"
          :false-value="'inactive'"
          color="primary"
          hide-details
          :label="$t('ncrm.workflows.nodeSettings.active')"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="form.mode"
          :items="enrichmentModeOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.enrichment.mode')"
          hide-details="auto"
          :menu-props="selectMenuProps"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="form.maxRowsPerRun"
          type="number"
          min="1"
          max="100"
          :label="$t('ncrm.workflows.nodeSettings.enrichment.maxRowsPerRun')"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="8">
        <v-select
          v-model="form.promptKey"
          :items="promptOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.enrichment.prompt')"
          hide-details="auto"
          :loading="loadingAiPrompts"
          :menu-props="selectMenuProps"
          @update:model-value="$emit('apply-selected-prompt-profile')"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-if="form.mode === 'automatic'"
          v-model="form.autoRunIntervalMinutes"
          :items="intervalOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.enrichment.autoRunInterval')"
          hide-details="auto"
          :menu-props="selectMenuProps"
        />
        <v-text-field
          v-else
          :model-value="$t('ncrm.workflows.nodeSettings.enrichment.manualOnly')"
          :label="$t('ncrm.workflows.nodeSettings.enrichment.execution')"
          hide-details="auto"
          readonly
        />
      </v-col>
    </v-row>

    <v-card v-if="selectedPrompt" variant="tonal" color="neutral" class="pa-3">
      <div class="text-caption text-medium-emphasis mb-2">
        {{ $t("ncrm.workflows.nodeSettings.enrichment.selectedPrompt") }}
      </div>
      <div class="text-body-2 font-weight-medium">{{ selectedPrompt.display_name || selectedPrompt.prompt_key }}</div>
      <div class="d-flex flex-wrap ga-2 mt-2">
        <v-chip v-for="meta in selectedPromptMeta" :key="meta.key" size="x-small" :color="meta.color">
          {{ meta.label }}
        </v-chip>
      </div>
    </v-card>

    <v-combobox
      v-model="form.targetFields"
      :items="targetFieldOptions"
      item-title="label"
      item-value="value"
      multiple
      chips
      closable-chips
      :label="$t('ncrm.workflows.nodeSettings.enrichment.targetFields')"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />

    <v-switch
      v-model="form.rerunDedupAfterEnrichment"
      color="primary"
      hide-details
      :label="$t('ncrm.workflows.nodeSettings.enrichment.rerunDedupAfterEnrichment')"
    />

    <v-alert v-if="enrichmentError" type="warning" density="compact">
      {{ enrichmentError }}
    </v-alert>

    <v-textarea
      v-model="form.description"
      :label="$t('ncrm.workflows.nodeSettings.description')"
      rows="3"
      hide-details="auto"
    />
  </div>
</template>

<script>
export default {
  name: "NcrmWorkflowDataEnrichmentSettings",
  props: {
    form: { type: Object, required: true },
    selectMenuProps: { type: Object, required: true },
    intervalOptions: { type: Array, default: () => [] },
    enrichmentModeOptions: { type: Array, default: () => [] },
    promptOptions: { type: Array, default: () => [] },
    selectedPrompt: { type: Object, default: null },
    selectedPromptMeta: { type: Array, default: () => [] },
    targetFieldOptions: { type: Array, default: () => [] },
    enrichmentError: { type: String, default: "" },
    loadingAiPrompts: { type: Boolean, default: false },
    aiPromptsError: { type: String, default: "" },
  },
  emits: ["open-ai-workspace", "refresh-ai-prompts", "apply-selected-prompt-profile"],
};
</script>
